/**
 * 공통 포맷 유틸리티
 *
 * 표 컬럼의 type 으로도 쓰이지만, 독립 함수라 어디서든 import 해서 재사용할 수 있다.
 *
 * @example
 * import { comma, tel, bizNum } from "@linkup/admin-ui";
 * comma(1234567)        // "1,234,567"
 * tel("01012345678")    // "010-1234-5678"
 * bizNum("1234567890")  // "123-45-67890"
 */

type Numeric = number | string | null | undefined;

/** 숫자에 천 단위 콤마를 넣는다. (소수점은 유지) */
export function comma(value: Numeric): string {
  if (value === null || value === undefined || value === "") return "";
  const num =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString("en-US", { maximumFractionDigits: 20 });
}

/** 전화번호에 하이픈을 넣는다. (휴대폰 · 서울(02) · 지역번호 · 대표번호 대응) */
export function tel(value: Numeric): string {
  if (value === null || value === undefined || value === "") return "";
  const d = String(value).replace(/\D/g, "");

  // 서울 지역번호 02
  if (d.startsWith("02")) {
    if (d.length === 9) return d.replace(/(\d{2})(\d{3})(\d{4})/, "$1-$2-$3");
    if (d.length === 10) return d.replace(/(\d{2})(\d{4})(\d{4})/, "$1-$2-$3");
  }
  // 대표번호 등 8자리 (1588-1234)
  if (d.length === 8) return d.replace(/(\d{4})(\d{4})/, "$1-$2");
  // 10자리: 0XX-XXX-XXXX
  if (d.length === 10) return d.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
  // 11자리: 0XX-XXXX-XXXX (휴대폰 등)
  if (d.length === 11) return d.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");

  // 형식을 모르면 원본 그대로 반환
  return String(value);
}

/** 사업자등록번호에 하이픈을 넣는다. (10자리 → XXX-XX-XXXXX) */
export function bizNum(value: Numeric): string {
  if (value === null || value === undefined || value === "") return "";
  const d = String(value).replace(/\D/g, "");
  if (d.length === 10) return d.replace(/(\d{3})(\d{2})(\d{5})/, "$1-$2-$3");
  return String(value);
}

/** DB에서 온 값(Date 객체 · "YYYY-MM-DD HH:mm:ss" 문자열 등)을 Date로 정규화한다. 파싱 실패 시 null. */
function toDate(value: unknown): Date | null {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(d.getTime()) ? null : d;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** 날짜만 "YYYY-MM-DD" 로 포맷한다. (시간은 버림) */
export function formatDate(value: unknown): string {
  const d = toDate(value);
  if (!d) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 날짜+시간을 "YYYY-MM-DD HH:mm:ss" 로 포맷한다. */
export function formatDateTime(value: unknown): string {
  const d = toDate(value);
  if (!d) return "";
  return `${formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** 표 컬럼 type 에서 사용할 수 있는 포맷 종류 */
export type ColumnFormatType = "comma" | "tel" | "bizNum" | "date" | "datetime";

/** type 값에 맞는 포맷 함수를 적용한다. (AdminTable 내부에서 사용) */
export function formatByType(type: ColumnFormatType, value: unknown): string {
  switch (type) {
    case "comma":
      return comma(value as Numeric);
    case "tel":
      return tel(value as Numeric);
    case "bizNum":
      return bizNum(value as Numeric);
    case "date":
      return formatDate(value);
    case "datetime":
      return formatDateTime(value);
    default:
      return value == null ? "" : String(value);
  }
}
